import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { TasksKanbanBoard } from './TasksKanbanBoard'
import { groupTasksByTeam } from './team-grouping'
import type { TaskRow, TeamGroup } from '@/types/work'

interface StackedTeamBoardsProps {
  tasks: TaskRow[]
  teamGroups: TeamGroup[]
  onTaskClick: (id: number) => void
}

// US16 U7 — the grouped Kanban variant: one TasksKanbanBoard per
// non-empty team (server-ordered via team_groups), stacked vertically
// under collapsible team headers. No board changes — each instance runs
// its own groupTasksForBoard, per-status caps, and drag context. team_id
// is immutable, so a card can never need to migrate between boards.
export function StackedTeamBoards({ tasks, teamGroups, onTaskClick }: StackedTeamBoardsProps) {
  const sections = groupTasksByTeam(tasks, teamGroups)

  return (
    <div className="space-y-4">
      {sections.map(({ group, tasks: groupTasks }) => (
        <TeamBoardSection
          key={group.id}
          group={group}
          tasks={groupTasks}
          onTaskClick={onTaskClick}
        />
      ))}
    </div>
  )
}

function TeamBoardSection({
  group,
  tasks,
  onTaskClick,
}: {
  group: TeamGroup
  tasks: TaskRow[]
  onTaskClick: (id: number) => void
}) {
  const [open, setOpen] = useState(true)

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted/50"
        // depth indents nested teams to mirror the grouped table.
        style={{ paddingLeft: `${0.5 + group.depth * 1.5}rem` }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium">{group.name}</span>
        <Badge variant="secondary">{group.count}</Badge>
      </button>

      {open && <TasksKanbanBoard tasks={tasks} onTaskClick={onTaskClick} />}
    </div>
  )
}
